import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { markManualLanguage } from "@/lib/languageDetection";

const languages = [
  { code: "pt", label: "Português", flag: "🇦🇴" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];

interface LanguageSelectorProps {
  className?: string;
  variant?: "desktop" | "mobile" | "minimal";
}

export function LanguageSelector({ className, variant = "desktop" }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [activeLanguage, setActiveLanguage] = useState(() => (i18n.resolvedLanguage || i18n.language || "pt").split("-")[0]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const currentLang = languages.find((l) => l.code === activeLanguage) || languages[0];

  useEffect(() => {
    const syncLanguage = (lng: string) => setActiveLanguage((lng || "pt").split("-")[0]);
    syncLanguage(i18n.resolvedLanguage || i18n.language);
    i18n.on("languageChanged", syncLanguage);

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      i18n.off("languageChanged", syncLanguage);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [i18n]);

  const handleLanguageChange = async (code: string) => {
    setActiveLanguage(code);
    markManualLanguage(code);
    document.documentElement.lang = code;
    await i18n.changeLanguage(code);
    setIsOpen(false);
  };

  if (variant === "minimal") {
    return (
      <div className={cn("relative", className)} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          aria-label="Select language"
        >
          <Globe className="w-5 h-5 text-gray-700 dark:text-gray-200" />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-48 rounded-2xl bg-white dark:bg-gray-900 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 dark:border-gray-800 p-2 z-[100]"
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                    currentLang.code === lang.code
                      ? "bg-primary/10 text-primary dark:text-primary-foreground"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  )}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="text-lg">{lang.flag}</span>
                    {lang.label}
                  </span>
                  {currentLang.code === lang.code && <Check className="w-4 h-4" />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative flex items-center gap-2 px-3 py-2 rounded-full border bg-white dark:bg-gray-900 transition-all duration-300 group",
          !user 
            ? "border-primary/50 dark:border-primary/50 shadow-[0_0_10px_rgba(232,81,61,0.2)] hover:shadow-[0_0_20px_rgba(232,81,61,0.4)]" 
            : "border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md"
        )}
        aria-expanded={isOpen}
      >
        {/* Highly visible pulsing effect only for non-logged-in users */}
        {!user && (
          <>
            <div className="absolute inset-0 rounded-full border-2 border-primary/60 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] opacity-75 pointer-events-none" />
            <div className="absolute inset-0 rounded-full border border-primary/40 animate-pulse pointer-events-none" />
          </>
        )}
        
        <span className="relative z-10 text-lg leading-none transition-transform duration-300 group-hover:scale-110 drop-shadow-sm">{currentLang.flag}</span>
        {variant !== "mobile" && (
          <span className="relative z-10 flex items-center text-sm font-semibold text-gray-700 dark:text-gray-200 hidden sm:flex group-hover:text-primary transition-colors">
            {currentLang.label}
            <ChevronDown className="w-4 h-4 ml-1 opacity-60 group-hover:opacity-100 transition-opacity" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn(
              "absolute mt-3 rounded-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-2xl border border-white/20 dark:border-gray-700/50 p-2 z-[100] min-w-[200px]",
              variant === "mobile" ? "right-[-10px] origin-top-right" : "right-0 origin-top-right"
            )}
          >
            {languages.map((lang, index) => (
              <motion.button
                key={lang.code}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                  currentLang.code === lang.code
                    ? "bg-[#E8513D] text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                )}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg drop-shadow-sm">{lang.flag}</span>
                  {lang.label}
                </span>
                {currentLang.code === lang.code && <Check className="w-4 h-4 text-white" />}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
